import axiosInstance from './axios';
import CloverPointPayment from 'types/models/CloverPointPayment';
import FiservQrPayment from 'types/models/FiservQrPayment';
import CloverPoint from 'types/models/CloverPoint';
import PaymentMethodReportPayload from 'types/requests/PaymentMethodReportPayload';
import IzipayPayment from 'types/models/IzipayPayment';
import CloverConnectionsForm from 'types/forms/CloverConnectionsForm';
import ByMethodPaymentResult from 'types/models/ByMethodPaymentResult';

export const parseFDPayment = (fdPayment: any) => {
  return {
    id: fdPayment.id,
    chargeTotal: fdPayment.charge_total,
    currency: fdPayment.currency,
    timeZone: fdPayment.time_zone,
    ccBrand: fdPayment.cc_brand,
    ccCountry: fdPayment.cc_country,
    ccExpMonth: fdPayment.cc_exp_month,
    ccExpYear: fdPayment.cc_exp_year,
    ccLastFourDigits: fdPayment.cc_last_four_digits,
    ccOwnerName: fdPayment.cc_owner_name,
    ccBin: fdPayment.cc_bin,
    approvalCode: fdPayment.approval_code,
    cardNumber: fdPayment.card_number,
    dues: fdPayment.dues,
    oid: fdPayment.oid,
    refNumber: fdPayment.ref_number,
    status: fdPayment.status,
    terminalId: fdPayment.terminal_id,
    txnDateProcessed: fdPayment.txn_date_processed,
    txnDatetime: fdPayment.txn_datetime,
    txnType: fdPayment.txn_type,
  };
};

export const parseCloverPointPayment = (cloverPointPayment: any): CloverPointPayment => {
  return {
    id: cloverPointPayment.id,
    amount: cloverPointPayment.amount,
    orderId: cloverPointPayment.order_id,
    state: cloverPointPayment.state,
    paymentState: cloverPointPayment.payment_state,
    externalPaymentId: cloverPointPayment.external_payment_id,
    paymentCardLabel: cloverPointPayment.payment_card_type,
    paymentMethod: cloverPointPayment.payment_method,
    cardBrand: cloverPointPayment.card_brand,
    cardLastFourDigits: cloverPointPayment.card_last_four_digits,
    cardHolderName: cloverPointPayment.cardholder_name,
    device: cloverPointPayment.device ? parseCloverPoint(cloverPointPayment.device) : undefined,
    hostTransactionId: cloverPointPayment.host_transaction_id ?? undefined,
  };
};

export const parseIzipayPayment = (izipayPayment: any): IzipayPayment => {
  return {
    id: izipayPayment.id,
    code: izipayPayment.code,
    message: izipayPayment.message,
    currency: izipayPayment.currency,
    amount: izipayPayment.amount,
    orderId: izipayPayment.order_id,
    orderNumber: izipayPayment.order_number,
    cardBrand: izipayPayment.card_brand,
    cardNumber: izipayPayment.card_number,
    codeAuth: izipayPayment.code_auth,
    signature: izipayPayment.signature,
  };
};

const parseCloverPoint = (data: any): CloverPoint => {
  return {
    id: data.id,
    name: data.name ?? '',
    description: data.description ?? '',
    isPaired: data.is_paired === 1,
    serial: data.serial ?? '',
    deviceAddress: data.device_address ?? '',
    deviceLastSuccessfulConnection: data.device_last_successful_connection ?? '',
    deviceConnectionQuality: data.device_connection_quality ?? 0,
    deviceConnectionStatus: data.device_connection_status ?? '',
    deviceConnectionType: data.device_connection_type ?? '',
    pairingOrigin: data.pairing_origin ?? '',
    pairingOriginCode: data.pairing_origin_code ?? '',
    deviceHasPaper: data.device_has_paper ?? false,
    isActive: data.is_active ?? false,
  };
};

const parseCloverPoints = (data: any): CloverPoint[] => {
  return data.map((item: any): CloverPoint => parseCloverPoint(item));
};

export const parseFiservQrPayment = (fiservQrPayment: any): FiservQrPayment => {
  return {
    id: fiservQrPayment.id,
    device: parseCloverPoint(fiservQrPayment.device),
  };
};

export const getAvailableCloverPoints = async (cloverPointConfig: string): Promise<CloverPoint[]> => {
  try {
    const { data } = await axiosInstance({
      method: 'GET',
      url: `clover-point/config/${cloverPointConfig}/available-clover-points`,
    });
    return parseCloverPoints(data);
  } catch (error) {
    console.log(error);
    return [];
  }
};

export const getPaymentIdsByMethod = async (
  payload: PaymentMethodReportPayload,
  dateFrom: string,
  dateTo: string,
): Promise<ByMethodPaymentResult[]> => {
  try {
    const { data } = await axiosInstance({
      method: 'POST',
      url: 'payments/get/ids-by-method',
      data: { ...payload, dateFrom, dateTo },
    });
    return parseByMethodPaymentResult(data);
  } catch (error) {
    console.log(error);
    return [];
  }
};

const parseByMethodPaymentResult = (data: any[]):ByMethodPaymentResult[]  => {
  return data.map((item) => ({
    paymentId: item.id,
    amount: parseFloat(item.amount),
    isSplit: Boolean(item.is_split)
  }));
}

export const getCloverPointsByCodes = async ({
  codes,
  connectionStatuses,
  connectionTypes,
  getAll = false,
}: CloverConnectionsForm): Promise<CloverPoint[]> => {
  try {
    const { data } = await axiosInstance({
      method: "POST",
      url: "clover-point/by-codes",
      data: {
        codes,
        connectionStatuses,
        connectionTypes,
        getAll,
      },
    });

    return parseCloverPoints(data);
  } catch (error) {
    console.error(error);
    return []
  }
};
